import React, { useState } from 'react';
import { Button, TextField, Alert } from '@mui/material';

interface TipSelectionProps {
  subtotal: number;
  onTipChange: (tipAmount: number, tipPercent: number | null) => void;
  className?: string;
}

const TipSelection: React.FC<TipSelectionProps> = ({ subtotal, onTipChange, className = '' }) => {
  const [selectedTipPercent, setSelectedTipPercent] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<string>('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [error, setError] = useState<string>('');
  const [showLargeTipWarning, setShowLargeTipWarning] = useState(false);

  const tipPercentages = [15, 18, 20];

  const handlePercentageTip = (percent: number) => {
    setShowCustomInput(false);
    setCustomTipAmount('');
    setError('');
    setSelectedTipPercent(percent);
    const tipAmount = Math.round((subtotal * percent) / 100);
    onTipChange(tipAmount, percent);
  };

  const handleCustomTip = () => {
    setShowCustomInput(true);
    setSelectedTipPercent(null);
    setError('');
  };

  const applyCustomTip = () => {
    const amount = parseFloat(customTipAmount);

    if (isNaN(amount) || amount < 0) {
      setError('Please enter a valid tip amount');
      return;
    }

    if (amount > subtotal) {
      setShowLargeTipWarning(true);
      return;
    }

    const roundedAmount = Math.round(amount);
    setError('');
    setSelectedTipPercent(null);
    onTipChange(roundedAmount, null);
    setShowCustomInput(false);
  };

  const confirmLargeTip = () => {
    const amount = Math.round(parseFloat(customTipAmount));
    setError('');
    setSelectedTipPercent(null);
    onTipChange(amount, null);
    setShowCustomInput(false);
    setShowLargeTipWarning(false);
  };

  const handleNoTip = () => {
    setShowCustomInput(false);
    setCustomTipAmount('');
    setError('');
    setSelectedTipPercent(null);
    onTipChange(0, 0);
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div className={`tip-selection ${className}`}>
      <h3 className="text-lg font-semibold mb-3">Add a tip</h3>
      
      <div 
        data-testid="tip-selection-container"
        className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 mb-4`}
      >
        {tipPercentages.map((percent) => {
          const tipAmount = Math.round((subtotal * percent) / 100);
          return (
            <Button
              key={percent}
              variant={selectedTipPercent === percent ? "contained" : "outlined"}
              onClick={() => handlePercentageTip(percent)}
              className={`${isMobile ? 'w-full' : ''}`}
            >
              {percent}%
            </Button>
          );
        })}
        
        <Button
          variant={showCustomInput ? "contained" : "outlined"}
          onClick={handleCustomTip}
          className={`${isMobile ? 'w-full' : ''}`}
        >
          Custom
        </Button>
        
        <Button
          variant={selectedTipPercent === 0 ? "contained" : "outlined"}
          onClick={handleNoTip}
          className={`${isMobile ? 'w-full' : ''}`}
        >
          No tip
        </Button>
      </div>

      {showCustomInput && (
        <div className="flex gap-2 mb-4">
          <TextField
            type="number"
            placeholder="Enter tip amount"
            value={customTipAmount}
            onChange={(e) => setCustomTipAmount(e.target.value)}
            size="small"
            inputProps={{ min: 0, step: 0.01 }}
            className="flex-1"
          />
          <Button variant="contained" onClick={applyCustomTip}>
            Apply
          </Button>
        </div>
      )}

      {error && (
        <Alert severity="error" className="mb-3">
          {error}
        </Alert>
      )}

      {showLargeTipWarning && (
        <Alert severity="warning" className="mb-3">
          <div>This tip is more than 100% of the bill</div>
          <Button 
            variant="contained" 
            size="small" 
            onClick={confirmLargeTip}
            className="mt-2"
          >
            Confirm large tip
          </Button>
        </Alert>
      )}
    </div>
  );
};

export default TipSelection;